import { Dispatch } from "redux";
import { ParticipationLoadError } from "@Interfaces";
import {
    ParticipationApiError,
    ParticipationService,
} from "@Services/API/Participation";
import { actions } from "./index";

const {
    fetchParticipationStart,
    fetchParticipationSuccess,
    fetchParticipationFailure,
    setAttendeeCountStart,
    setAttendeeCountSuccess,
    setAttendeeCountFailure,
    clearAttendeeCountError,
} = actions;

// Documented errors of GET /v1/groups/:group_id/participation.
const toLoadError = (error: unknown): ParticipationLoadError => {
    if (error instanceof ParticipationApiError) {
        if (error.status === 403 && error.message === "channel_ended") {
            return "closed";
        }
        if (
            error.status === 409 &&
            error.message === "participation_parent_not_supported"
        ) {
            return "parent";
        }
    }
    return "generic";
};

// Polling can overlap fetches; the store applies only the latest one.
let lastRequestId = 0;

const ParticipationActions = {
    fetchParticipationSummary:
        (channelId: string) => async (dispatch: Dispatch) => {
            const requestId = ++lastRequestId;
            dispatch(fetchParticipationStart({ channelId, requestId }));

            try {
                const summary = await ParticipationService.getSummary(
                    channelId
                );
                dispatch(fetchParticipationSuccess({ requestId, summary }));
            } catch (error) {
                dispatch(
                    fetchParticipationFailure({
                        requestId,
                        error: toLoadError(error),
                    })
                );
            }
        },

    /** Resolves true on success; `fallbackMessage` is shown when the API sends none. */
    setAttendeeCount:
        (channelId: string, count: number, fallbackMessage: string) =>
        async (dispatch: Dispatch) => {
            dispatch(setAttendeeCountStart());

            try {
                const result = await ParticipationService.setAttendeeCount(
                    channelId,
                    count
                );
                dispatch(setAttendeeCountSuccess({ channelId, result }));
                return true;
            } catch (error) {
                // 400 validation messages are display text ("Can't be fewer than…").
                const message =
                    error instanceof ParticipationApiError &&
                    error.status === 400 &&
                    error.message
                        ? error.message
                        : fallbackMessage;
                dispatch(setAttendeeCountFailure({ channelId, message }));
                return false;
            }
        },

    clearAttendeeCountError: () => (dispatch: Dispatch) => {
        dispatch(clearAttendeeCountError());
    },
};

export default ParticipationActions;
